/**
 * Shared pieces of the standard chart set (design system guideline 11-charts.md): how much data a
 * chart needs, round-number scales, and the colour roles every chart draws with. Charts lay out in
 * HTML and CSS by percentages (SVG only for line shapes), so they render without JavaScript and
 * their text stays readable at any width.
 */

/**
 * How many values a chart needs before it may show a figure (agreed 2026-10-11, building on
 * MIN_CHART_SAMPLE and MIN_RANK_SAMPLE in utils/salary/salary-hub-insights.ts). Below the minimum a
 * chart shows its "not enough data" state, never a guess.
 */
export const CHART_MINIMUM = {
  /** A median or average for one item. */
  median: 10,
  /** A middle half (p25 to p75) or a histogram. */
  spread: 20,
  /** An item in a ranking or league table. */
  rank: 30,
  /** A heatmap cell or a map region. */
  cell: 10,
} as const;

export type ChartMinimumKind = keyof typeof CHART_MINIMUM;

export const hasEnoughData = (sample: number, kind: ChartMinimumKind): boolean => sample >= CHART_MINIMUM[kind];

/** A scale with round-number ticks that covers [min, max]; `zero` forces the scale to start at 0. */
export function niceScale(min: number, max: number, { count = 4, zero = false } = {}) {
  let lo = zero ? Math.min(0, min) : min;
  let hi = max;
  if (hi === lo) hi = lo + 1;
  const raw = (hi - lo) / count;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / magnitude;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * magnitude;
  lo = Math.floor(lo / step) * step;
  hi = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Number(v.toFixed(10)));
  return { min: lo, max: hi, ticks };
}

/** A scale over exactly [min, max] (charts sharing one scale), with round ticks inside it. */
export function fixedScale(min: number, max: number, count = 4) {
  const { ticks } = niceScale(min, max, { count });
  return { min, max, ticks: ticks.filter((t) => t >= min - 1e-9 && t <= max + 1e-9) };
}

/** Where a value sits on a scale, 0 to 100, clamped. */
export function percentOf(value: number, min: number, max: number): number {
  if (max === min) return 0;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

/** Whole-number percentages of a total that add up to exactly 100 (largest remainder). */
export function roundedShares(values: number[]): number[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  if (!total) return values.map(() => 0);
  const exact = values.map((v) => (Math.max(0, v) / total) * 100);
  const floors = exact.map(Math.floor);
  let left = 100 - floors.reduce((s, v) => s + v, 0);
  const order = exact.map((v, i) => ({ i, r: v - Math.floor(v) })).sort((a, b) => b.r - a.r);
  for (const { i } of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return floors;
}

/** Which tick labels to show so at most `max` appear, always keeping the first and last. */
export function sparseLabels(count: number, max = 6): boolean[] {
  const step = Math.max(1, Math.ceil((count - 1) / (max - 1)));
  return Array.from(
    { length: count },
    (_, i) => i === 0 || i === count - 1 || (i % step === 0 && count - 1 - i >= step / 2),
  );
}

/**
 * A chart series colour. `highlight` is the finding (sun), `context` everything else, `subject`
 * the reader's own thing (ink), 1 to 6 the categorical order for equal series, and the work-model
 * colours, which never take the categorical order.
 */
export type ChartSeries = 'highlight' | 'context' | 'subject' | 1 | 2 | 3 | 4 | 5 | 6 | 'remote' | 'hybrid' | 'onsite';

const FILL: Record<string, string> = {
  highlight: 'bg-jc-chart-2',
  context: 'bg-jc-chart-context',
  subject: 'bg-jc-ink',
  1: 'bg-jc-chart-1',
  2: 'bg-jc-chart-2',
  3: 'bg-jc-chart-3',
  4: 'bg-jc-chart-4',
  5: 'bg-jc-chart-5',
  6: 'bg-jc-chart-6',
  remote: 'bg-jc-work-remote',
  hybrid: 'bg-jc-work-hybrid',
  onsite: 'bg-jc-work-onsite',
};
const STROKE: Record<string, string> = {
  highlight: 'stroke-jc-ink',
  context: 'stroke-jc-chart-context',
  subject: 'stroke-jc-ink',
  1: 'stroke-jc-chart-1',
  2: 'stroke-jc-chart-2',
  3: 'stroke-jc-chart-3',
  4: 'stroke-jc-chart-4',
  5: 'stroke-jc-chart-5',
  6: 'stroke-jc-chart-6',
  remote: 'stroke-jc-work-remote',
  hybrid: 'stroke-jc-work-hybrid',
  onsite: 'stroke-jc-work-onsite',
};
export const chartFill = (series: ChartSeries): string => FILL[String(series)];
export const chartStroke = (series: ChartSeries): string => STROKE[String(series)];

/** The sequential ramp, fewer to more, and the text colour that reads on each step. */
export const SEQ_FILL = [
  'bg-jc-chart-seq-1',
  'bg-jc-chart-seq-2',
  'bg-jc-chart-seq-3',
  'bg-jc-chart-seq-4',
  'bg-jc-chart-seq-5',
  'bg-jc-chart-seq-6',
] as const;
export const SEQ_TEXT = [
  'text-jc-chart-seq-on-1',
  'text-jc-chart-seq-on-2',
  'text-jc-chart-seq-on-3',
  'text-jc-chart-seq-on-4',
  'text-jc-chart-seq-on-5',
  'text-jc-chart-seq-on-6',
] as const;

/**
 * On narrow charts (container under 28rem) every other scale label hides; the first and last
 * stay. When the last falls between two kept labels, the one before it hides instead.
 */
export function thinTick(index: number, count: number): string | false {
  const last = count - 1;
  if (index === 0 || index === last) return false;
  const hide = index % 2 === 1 || (last % 2 === 1 && index === last - 1);
  return hide && '@max-md:hidden';
}

/** Value labels: the display face at 800 with tabular figures. */
export const VALUE_TEXT = 'font-display text-xs font-bold text-jc-ink tabular-nums whitespace-nowrap';
/** Category labels and ticks. */
export const LABEL_TEXT = 'text-xs text-jc-ink-secondary';
export const TICK_TEXT = 'text-xs text-jc-ink-secondary tabular-nums';
